import { logger } from '../../utils/logger';

// Define the modal class patterns to look for
export const MODAL_CLASS_PATTERNS = [
  // Specific modal patterns
  'LessonModal_global',
  'LessonModal_backdrop',
  'LessonModal_inner',
  'LessonPreView',
  'LessonProgressModal',
  'UnitModal_global',
  'LessonProgressModal_wrapper',
  'LessonProgressModal_loading',
  // 'LessonProgressModal_backdrop',
  'modal_global',
  'modal_local',
  'Modal_global',
  'Modal_local',
  
  // Additional patterns for modals
  'ant-modal',
  'ReactModal',
  'modal-content',
  'modal-dialog',
  'modal-body',
  'modal-header'
];

// Build the modal selector from the class patterns
const buildModalSelector = () => {
  // Create selectors that specifically target elements, not stylesheets
  const classSelectors = MODAL_CLASS_PATTERNS.map(pattern => 
    `body [class*="${pattern}"]`  // Ensure we're only matching elements in the body
  );
  return [...classSelectors, 'body .modal-container', 'body [role="dialog"]'].join(', ');
};

// Define the modal selector for consistent use throughout the application
export const MODAL_SELECTOR = buildModalSelector();

// Define the supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'AR', name: 'Arabic' },
  { code: 'BG', name: 'Bulgarian' },
  { code: 'CS', name: 'Czech' },
  { code: 'DA', name: 'Danish' },
  { code: 'DE', name: 'German' },
  { code: 'EL', name: 'Greek' },
  { code: 'EN', name: 'English' },
  { code: 'EN_GB', name: 'English (British)' },
  { code: 'EN_US', name: 'English (American)' },
  { code: 'ES', name: 'Spanish' },
  { code: 'ET', name: 'Estonian' },
  { code: 'FA', name: 'Persian (Farsi)' },
  { code: 'FI', name: 'Finnish' },
  { code: 'FR', name: 'French' },
  { code: 'HU', name: 'Hungarian' },
  { code: 'ID', name: 'Indonesian' },
  { code: 'IT', name: 'Italian' },
  { code: 'JA', name: 'Japanese' },
  { code: 'KO', name: 'Korean' },
  { code: 'LT', name: 'Lithuanian' },
  { code: 'LV', name: 'Latvian' },
  { code: 'NB', name: 'Norwegian Bokmål' },
  { code: 'NL', name: 'Dutch' },
  { code: 'PA', name: 'Punjabi' },
  { code: 'PL', name: 'Polish' },
  { code: 'PT_BR', name: 'Portuguese (Brazilian)' },
  { code: 'PT_PT', name: 'Portuguese (European)' },
  { code: 'RO', name: 'Romanian' },
  { code: 'RU', name: 'Russian' },
  { code: 'SK', name: 'Slovak' },
  { code: 'SL', name: 'Slovenian' },
  { code: 'SV', name: 'Swedish' },
  { code: 'TL', name: 'Tagalog' },
  { code: 'TR', name: 'Turkish' },
  { code: 'UK', name: 'Ukrainian' },
  { code: 'UR', name: 'Urdu' },
  { code: 'ZH_HANS', name: 'Chinese Simplified' },
  { code: 'ZH_HANT', name: 'Chinese Traditional' }
];

/**
 * Helper function to normalize language codes
 * Converts hyphens to underscores (e.g., 'en-US' to 'en_US')
 */
export const normalizeLanguageCode = (lang: string): string => {
  return lang.replace(/-/g, '_');
};

/**
 * Debounce function to prevent multiple rapid executions
 * @param func The function to debounce
 * @param wait The wait time in milliseconds
 */
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: any[]) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
};

/**
 * Get all text nodes that should be translated
 * @param currentLanguage The current language
 * @param processedNodes Set of already processed nodes
 */
export const getTextNodesToTranslate = (currentLanguage: string): Node[] => {
  const textNodes: Node[] = [];
  
  // Get the current language for checking data-translated-lang attribute
  const normalizedCurrentLang = normalizeLanguageCode(currentLanguage);
  
  // Create a tree walker to find all text nodes
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        // Skip empty or whitespace-only text nodes
        if (!node.textContent?.trim()) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes in scripts, styles, and other non-visible elements
        const parent = node.parentElement;
        if (!parent) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Get node details for debugging
        const nodeLanguage = parent.getAttribute('data-translated-lang');
        // const hasOriginalText = parent.hasAttribute('data-original-text');
        // const originalText = parent.getAttribute('data-original-text');
                
        // CRITICAL: Only translate nodes that are in English or have no language attribute
        // This ensures we only translate from English to another language
        if (nodeLanguage && nodeLanguage !== 'EN') {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes that have already been translated to the current language
        if (nodeLanguage === normalizedCurrentLang) {
          return NodeFilter.FILTER_REJECT;
        }
        
        const tagName = parent.tagName.toLowerCase();
        if (['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes that are part of the translation UI
        if (parent.closest('.translation-badge') || parent.closest('.translation-controls')) {
          return NodeFilter.FILTER_REJECT;
        }
        
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  // Collect all text nodes
  let currentNode;
  // eslint-disable-next-line no-cond-assign
  while (currentNode = walker.nextNode()) {
    textNodes.push(currentNode);
  }
  
  return textNodes;
};

/**
 * Get modal-specific text nodes
 * @param currentLanguage The current language
 */
export const getModalTextNodes = (currentLanguage: string): Node[] => {
  const textNodes: Node[] = [];
  
  // Get the current language for checking data-translated-lang attribute
  const normalizedCurrentLang = normalizeLanguageCode(currentLanguage);
  
  // Find all modal elements using the shared selector
  const modals = document.querySelectorAll(MODAL_SELECTOR);

  
  modals.forEach(modal => {
    // Create a tree walker for each modal
    const walker = document.createTreeWalker(
      modal,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Skip empty or whitespace-only text nodes
          if (!node.textContent?.trim()) {
            return NodeFilter.FILTER_REJECT;
          }
          
          // Skip nodes in scripts, styles, etc.
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          // Skip nodes that have already been translated to the current language
          if (parent.getAttribute('data-translated-lang') === normalizedCurrentLang) {
            return NodeFilter.FILTER_REJECT;
          }
          
          const tagName = parent.tagName.toLowerCase();
          if (['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName)) {
            return NodeFilter.FILTER_REJECT;
          }
          
          // Skip translation UI elements
          if (parent.closest('.translation-badge') || parent.closest('.translation-controls')) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    
    // Collect all text nodes in this modal
    let currentNode;
    // eslint-disable-next-line no-cond-assign
    while (currentNode = walker.nextNode()) {
      textNodes.push(currentNode);
    }
  });
  
  return textNodes;
};

/**
 * Get all text nodes in the document
 * @param currentLanguage The current language
 * @param processedNodes Set of already processed nodes
 */
export const getAllTextNodes = (currentLanguage: string, processedNodes: WeakSet<Node>): Node[] => {
  const textNodes: Node[] = [];
  
  // Get the current language for checking data-translated-lang attribute
  const normalizedCurrentLang = normalizeLanguageCode(currentLanguage);
  
  // Create a tree walker for the entire document
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (textNode) => {
        // Skip empty or whitespace-only text nodes
        if (!textNode.textContent?.trim()) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip already processed nodes to avoid duplicate translations
        if (processedNodes.has(textNode)) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes in scripts, styles, and other non-visible elements
        const parent = textNode.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        
        // CRITICAL: Only translate nodes that are in English or have no language attribute
        // This ensures we only translate from English to another language
        const nodeLanguage = parent.getAttribute('data-translated-lang');
        if (nodeLanguage && nodeLanguage !== 'EN') {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes that have already been translated to the current language
        if (nodeLanguage === normalizedCurrentLang) {
          return NodeFilter.FILTER_REJECT;
        }
        
        const tagName = parent.tagName.toLowerCase();
        if (['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes that are part of the translation UI
        if (parent.closest('.translation-badge') || parent.closest('.translation-controls')) {
          return NodeFilter.FILTER_REJECT;
        }
        
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  let textNode;
  // eslint-disable-next-line no-cond-assign
  while (textNode = walker.nextNode()) {
    textNodes.push(textNode);
  }
  
  return textNodes;
};

/**
 * Save the translation cache to localStorage
 * @param translationCache The translation cache to save
 */
export const saveTranslationCache = (translationCache: Map<string, Map<string, string>>) => {
  try {
    // Convert the Map to a JSON-serializable object
    const cacheObject: Record<string, Record<string, string>> = {};
    
    translationCache.forEach((langCache, lang) => {
      cacheObject[lang] = {};
      langCache.forEach((translation, text) => {
        cacheObject[lang][text] = translation;
      });
    });
    
    localStorage.setItem('translationCache', JSON.stringify(cacheObject));
    
    // // Log the size of each language cache
    // Object.entries(cacheObject).forEach(([lang, texts]) => {
    //   logger.info(`  ${lang}: ${Object.keys(texts).length} entries`);
    // });
  } catch (error) {
    logger.error('Error saving translation cache to localStorage:', error);
  }
};

/**
 * Load the translation cache from localStorage
 * @returns The loaded translation cache
 */
export const loadTranslationCache = (): Map<string, Map<string, string>> => {
  try {
    const storedCache = localStorage.getItem('translationCache');
    if (storedCache) {
      // Convert the JSON object back to a Map
      const parsedCache = JSON.parse(storedCache);
      const newCache = new Map<string, Map<string, string>>();
      
      Object.entries(parsedCache).forEach(([lang, texts]) => {
        newCache.set(lang, new Map(Object.entries(texts as Record<string, string>)));
      });
      
      logger.info('Loaded translation cache from localStorage');
      return newCache;
    }
  } catch (error) {
    logger.error('Error loading translation cache from localStorage:', error);
  }
  
  return new Map();
};

/**
 * Filter nodes to only include text nodes that should be translated
 * @param nodes The nodes to filter
 * @param currentLanguage The current language
 * @param ignoreProcessedNodes Whether to ignore processed nodes
 * @param processedNodes Set of already processed nodes
 */
export const filterTextNodes = (
  nodes: Node[], 
  currentLanguage: string, 
  ignoreProcessedNodes: boolean, 
  processedNodes: WeakSet<Node>
): Node[] => {
  const requestedLanguage = normalizeLanguageCode(currentLanguage);
  
  return nodes.filter(node => {
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) {
      return false;
    }
    
    // Skip already processed nodes (unless we're ignoring processed nodes)
    if (!ignoreProcessedNodes && processedNodes.has(node)) {
      return false;
    }
    
    const parent = node.parentElement;
    if (!parent) return false;
    
    // CRITICAL: Only translate nodes that are in English or have no language attribute
    const nodeLanguage = parent.getAttribute('data-translated-lang');
    if (nodeLanguage && nodeLanguage !== 'EN') {
      return false;
    }
    
    // Skip nodes that have already been translated to the current language
    if (nodeLanguage === requestedLanguage) {
      return false;
    }
    
    const tagName = parent.tagName.toLowerCase();
    if (['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName)) {
      return false;
    }
    
    if (parent.closest('.translation-badge') || parent.closest('.translation-controls')) {
      return false;
    }
    
    return true;
  });
};
